import { Capacitor, registerPlugin } from "@capacitor/core";
import { useHeartRateStore } from "../stores/useHeartRateStore.js";
import { logEvent } from "../stores/useLogsStore.jsx";

let initialized = false;
let androidSampleHandle = null;
let androidStatusHandle = null;
let iosCommandInFlight = null;
let lastIOSStatusSignature = "";
let lastIOSStatusLogAt = 0;
let iosAutoLockedSource = null;

const IOS_COMMAND_SETTLE_MS = 1800;

const dispatchIOSSettled = async (action, extra = {}) => {
  if (iosCommandInFlight) return false;
  if (!dispatchIOS(action, extra)) return false;
  iosCommandInFlight = new Promise((resolve) => {
    window.setTimeout(resolve, IOS_COMMAND_SETTLE_MS);
  });
  try {
    await iosCommandInFlight;
  } finally {
    iosCommandInFlight = null;
  }
  return true;
};

const dispatchIOS = (action, extra = {}) => {
  const handler = window?.webkit?.messageHandlers?.nativeBridge;
  if (!handler?.postMessage) return false;
  handler.postMessage({ action, ...extra });
  return true;
};

const isIOSNative = () =>
  Capacitor.getPlatform() === "ios" ||
  !!window?.webkit?.messageHandlers?.nativeBridge?.postMessage;

const androidPlugin = () =>
  window?.Capacitor?.Plugins?.HeartRateNativePlugin ||
  window?.Capacitor?.Plugins?.HeartRateNative;

export const initNativeHeartRate = async () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  const store = useHeartRateStore.getState();

  window.addEventListener("native:heartRate", (event) => {
    const sample = event?.detail || {};
    const beforeAccepted = useHeartRateStore.getState().diagnostics?.accepted || 0;
    store.addSample(sample);
    const nextState = useHeartRateStore.getState();
    if (
      isIOSNative() &&
      nextState.preferredSource === "auto" &&
      !iosAutoLockedSource &&
      (nextState.diagnostics?.accepted || 0) > beforeAccepted &&
      nextState.source
    ) {
      iosAutoLockedSource = nextState.source;
      dispatchIOS("lockHeartRateSource", { source: iosAutoLockedSource });
      logEvent("info", "HEART_RATE_AUTO_SOURCE_LOCKED", {
        platform: "ios",
        source: iosAutoLockedSource,
        deviceName: nextState.deviceName || sample.deviceName || null,
      });
    }
  });
  window.addEventListener("native:heartRateStatus", (event) => {
    const detail = event?.detail || {};
    const current = useHeartRateStore.getState();
    const nextSignature = JSON.stringify([
      !!detail.supported,
      detail.permission || null,
      detail.status || null,
      detail.error || null,
    ]);
    const stateChanged = nextSignature !== JSON.stringify([
      !!current.supported,
      current.permission || null,
      current.status || null,
      current.error || null,
    ]);
    if (stateChanged) current.setCapability(detail);

    const now = Date.now();
    if (nextSignature !== lastIOSStatusSignature || now - lastIOSStatusLogAt >= 15000) {
      lastIOSStatusSignature = nextSignature;
      lastIOSStatusLogAt = now;
      logEvent("info", "HEART_RATE_NATIVE_STATUS", {
        platform: isIOSNative() ? "ios" : Capacitor.getPlatform(),
        supported: !!detail.supported,
        permission: detail.permission || null,
        status: detail.status || null,
        error: detail.error || null,
      });
    }
  });
  window.addEventListener("native:healthProfile", (event) => {
    const weightKg = Number(event?.detail?.weightKg);
    if (Number.isFinite(weightKg) && weightKg >= 30 && weightKg <= 300) {
      localStorage.setItem("trekkin_health_weight_kg", String(weightKg));
      window.dispatchEvent(new CustomEvent("trekkin:health-profile-updated", {
        detail: { weightKg, source: "healthkit" },
      }));
    }
  });

  if (!Capacitor.isNativePlatform() && !isIOSNative()) {
    store.setCapability({ supported: false, status: "unavailable" });
    return;
  }

  if (isIOSNative()) {
    dispatchIOS("heartRateStatus");
    return;
  }

  const plugin = androidPlugin();
  if (!plugin) {
    store.setCapability({ supported: false, status: "unavailable" });
    return;
  }
  androidSampleHandle = await plugin.addListener?.("heartRateSample", (sample) => {
    useHeartRateStore.getState().addSample(sample);
  });
  androidStatusHandle = await plugin.addListener?.("heartRateStatus", (status) => {
    useHeartRateStore.getState().setCapability(status);
    logEvent("info", "HEART_RATE_NATIVE_STATUS", {
      platform: "android",
      supported: !!status?.supported,
      permission: status?.permission || null,
      status: status?.status || null,
      error: status?.error || null,
    });
  });
  try {
    const status = await plugin.getStatus();
    store.setCapability(status);
  } catch (error) {
    store.setCapability({ supported: true, status: "error", error: error?.message });
  }
};

export const startNativeHeartRate = async ({ activity = "walking", preserveSession = false } = {}) => {
  await initNativeHeartRate();
  if (!preserveSession) iosAutoLockedSource = null;
  useHeartRateStore.getState().beginSession({ preserveSamples: preserveSession });
  const preferredSource = useHeartRateStore.getState().preferredSource || "auto";
  logEvent("info", "HEART_RATE_SESSION_START", {
    platform: Capacitor.getPlatform(),
    activity,
    preferredSource,
    preserveSession: !!preserveSession,
  });
  if (isIOSNative()) {
    if (!(await dispatchIOSSettled("startHeartRate", { activity, preferredSource }))) {
      useHeartRateStore.getState().setStatus("unavailable", "Native bridge unavailable");
    }
    return;
  }
  const plugin = androidPlugin();
  if (!plugin) return;
  try {
    await plugin.startScan({ preferredSource, resetSession: !preserveSession });
  } catch (error) {
    useHeartRateStore.getState().setStatus("error", error?.message || String(error));
  }
};

export const syncNativeHeartRateSamples = async ({ reason = "manual" } = {}) => {
  if (Capacitor.getPlatform() !== "android") return { recovered: 0, supported: false };
  await initNativeHeartRate();
  const plugin = androidPlugin();
  if (!plugin?.getSessionSnapshot) return { recovered: 0, supported: false };
  const before = useHeartRateStore.getState();
  try {
    const hasSparseRecoverySamples = (before.samples || []).some(
      (sample) => sample?.sparse === true
    );
    const sinceTs = hasSparseRecoverySamples ? 0 : Number(before.lastSampleAt) || 0;
    const snapshot = await plugin.getSessionSnapshot({ sinceTs });
    const samples = Array.isArray(snapshot?.samples) ? snapshot.samples : [];
    const recovered = useHeartRateStore.getState().mergeNativeSamples(samples);
    logEvent("info", "HEART_RATE_NATIVE_RECONCILED", {
      reason,
      fullSnapshot: hasSparseRecoverySamples,
      sinceTs,
      beforeSampleCount: before.samples?.length || 0,
      nativeSampleCount: Number(snapshot?.sampleCount || 0),
      returnedSampleCount: samples.length,
      recoveredSampleCount: recovered,
      afterSampleCount: useHeartRateStore.getState().samples?.length || 0,
    });
    return { recovered, supported: true, nativeSampleCount: Number(snapshot?.sampleCount || 0) };
  } catch (error) {
    logEvent("warn", "HEART_RATE_NATIVE_RECONCILE_FAILED", {
      reason,
      error: error?.message || String(error),
    });
    return { recovered: 0, supported: true, error: error?.message || String(error) };
  }
};

export const authorizeAppleHealth = async () => {
  await initNativeHeartRate();
  if (!isIOSNative()) return false;
  useHeartRateStore.getState().setStatus("requestingPermission");
  return dispatchIOS("authorizeHeartRate");
};

export const switchNativeHeartRateSource = async ({ activity = "walking" } = {}) => {
  await initNativeHeartRate();
  const preferredSource = useHeartRateStore.getState().preferredSource || "auto";
  useHeartRateStore.getState().setStatus("switching");
  if (isIOSNative()) {
    if (!(await dispatchIOSSettled("switchHeartRate", { activity, preferredSource }))) {
      if (iosCommandInFlight) return false;
      useHeartRateStore.getState().setStatus("unavailable", "Native bridge unavailable");
      return false;
    }
    return true;
  }
  const plugin = androidPlugin();
  if (!plugin) return false;
  try {
    await plugin.stopScan?.();
    await plugin.startScan?.({ preferredSource });
    return true;
  } catch (error) {
    useHeartRateStore.getState().setStatus("error", error?.message || String(error));
    return false;
  }
};

export const pauseNativeHeartRate = async () => {
  if (isIOSNative()) dispatchIOS("pauseHeartRate");
  else await androidPlugin()?.stopScan?.();
  useHeartRateStore.getState().setStatus("paused");
  logEvent("info", "HEART_RATE_SESSION_PAUSE", useHeartRateStore.getState().getDiagnostics());
};

export const resumeNativeHeartRate = async ({ activity = "walking" } = {}) => {
  await syncNativeHeartRateSamples({ reason: "resume_before_scan" });
  return switchNativeHeartRateSource({ activity });
};

export const stopNativeHeartRate = async ({ workout = null } = {}) => {
  await syncNativeHeartRateSamples({ reason: "stop_before_scan" });
  const before = useHeartRateStore.getState();
  logEvent("info", "HEART_RATE_NATIVE_STOP_REQUEST", {
    status: before.status,
    sampleCount: before.samples?.length || 0,
    diagnostics: before.getDiagnostics(),
  });
  if (isIOSNative()) dispatchIOS("stopHeartRate", workout ? { workout } : {});
  else {
    await androidPlugin()?.stopScan?.();
    if (workout && Capacitor.getPlatform() === "android") {
      const healthConnect = registerPlugin("HealthConnectNative");
      healthConnect.saveWorkout({ workout }).then((result) => {
        logEvent("info", "HEALTH_CONNECT_WORKOUT_SAVED", result || {});
      }).catch((error) => {
        // Health Connect is an optional destination: a refusal or an
        // unavailable service must never prevent the Trekkin activity saving.
        logEvent("warn", "HEALTH_CONNECT_WORKOUT_FAILED", {
          error: error?.message || String(error),
        });
      });
    }
  }
  await syncNativeHeartRateSamples({ reason: "stop_after_scan" });
  const summary = useHeartRateStore.getState().finishSession();
  if (Capacitor.getPlatform() === "android") {
    await androidPlugin()?.clearSession?.().catch(() => {});
  }
  iosAutoLockedSource = null;
  return summary;
};

export const disposeNativeHeartRate = async () => {
  await androidSampleHandle?.remove?.();
  await androidStatusHandle?.remove?.();
  androidSampleHandle = null;
  androidStatusHandle = null;
  initialized = false;
};
